// Rewards entry fee (migration 401) — the seller gate before the panel opens,
// and the admin list with waivers.
// Spec: docs/superpowers/specs/2026-11-03-rewards-program-design.md → "Entry fee flow"

import { apiUrl } from '../lib/api'

export type EntryFeeState = 'not_required' | 'due' | 'paid' | 'waived' | 'refunded'

export interface EntryFeeStatus {
  state: EntryFeeState
  amount_usd: string | number | null
  credit_usd: string | number | null
  trial_days: number | null
  failure_reason: string | null
  processing: boolean
}

export interface EntryFeePayment {
  client_secret: string
  publishable_key: string | null
  customer_session_client_secret: string | null
  amount_usd: string | number
}

export interface EntryFeeAdminRow {
  org_id: string
  org_name: string
  state: EntryFeeState
  amount_usd: string | number
  credit_usd: string | number
  trial_days: number
  stripe_payment_intent_id: string | null
  failure_reason: string | null
  paid_at: string | null
  refunded_at: string | null
  waived_reason: string | null
  created_at: string
}

export class EntryFeeApiError extends Error {
  readonly status: number
  readonly code: string | null

  constructor(message: string, status: number, code: string | null) {
    super(message)
    this.status = status
    this.code = code
  }
}

async function readJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      detail?: { message?: string; code?: string } | string
    }
    const detail = typeof body.detail === 'object' ? body.detail : undefined
    throw new EntryFeeApiError(detail?.message ?? `HTTP ${res.status}`, res.status, detail?.code ?? null)
  }
  return (await res.json()) as T
}

export const entryFeeKey = ['membership', 'entry-fee'] as const
export const adminEntryFeesKey = ['admin', 'rewards', 'entry-fees'] as const

export async function fetchEntryFee(): Promise<EntryFeeStatus> {
  return readJson(await fetch(apiUrl('/membership/entry-fee')))
}

export async function startEntryFeePayment(): Promise<EntryFeePayment> {
  return readJson(await fetch(apiUrl('/membership/entry-fee/payment'), { method: 'POST' }))
}

export async function fetchAdminEntryFees(): Promise<{ items: EntryFeeAdminRow[] }> {
  return readJson(await fetch(apiUrl('/admin/rewards/entry-fees')))
}

export async function waiveEntryFee(orgId: string, reason: string): Promise<EntryFeeAdminRow> {
  const res = await fetch(apiUrl(`/admin/rewards/entry-fees/${encodeURIComponent(orgId)}/waive`), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ reason }),
  })
  return readJson(res)
}
